import { RecommendedUsersProps, RecommendedUsersEditorProps } from './RecommendedUsers.types';

/**
 * 기존 데이터 형식을 새로운 형식으로 변환
 * 
 * @param originalProps 기존 컴포넌트의 props
 * @returns 변환된 새로운 컴포넌트용 props
 */
export function adaptRecommendedUsersProps(originalProps: any): RecommendedUsersProps {
  // 기본값 설정 (빈 객체로 초기화)
  const originalContent = originalProps?.content || {};
  const originalData = originalProps?.data || {};
  const originalComponentProps = originalProps?.componentProps || {};
  
  // content, data, componentProps에서 제목 추출
  const title = originalComponentProps?.title || 
                originalContent?.title || 
                originalData?.title || 
                '피추천인 목록';

  // 빈 데이터, 로딩, 로그인 필요 메시지 추출
  const emptyText = originalComponentProps?.emptyText || 
                   originalContent?.emptyText || 
                   '데이터가 없습니다';
  
  const loadingText = originalComponentProps?.loadingText || 
                     originalContent?.loadingText || 
                     '로딩 중...';
  
  const loginRequiredText = originalComponentProps?.loginRequiredText || 
                           originalContent?.loginRequiredText || 
                           '로그인이 필요합니다.';
  
  // 스타일 병합 (깊은 복사로 병합)
  const style = {
    ...(originalProps?.style || {}),
    ...(originalComponentProps?.style || {})
  };
  
  // 인라인 스타일과 CSS 클래스 통합
  if (originalProps.defaultStyle) {
    Object.assign(style, originalProps.defaultStyle);
  }

  // className 설정
  const className = originalProps?.className || originalComponentProps?.className || '';

  // 변환된 props 반환
  const adaptedProps: RecommendedUsersProps = {
    style,
    className,
    data: originalData,
    componentProps: {
      ...originalComponentProps,
      title,
      emptyText,
      loadingText,
      loginRequiredText,
      // 스킨 관련 속성 추가
      skin: originalComponentProps?.skin || 'basic',
      showTotalCount: originalComponentProps?.showTotalCount !== false,
      showUserIds: originalComponentProps?.showUserIds !== false,
      showUserNames: originalComponentProps?.showUserNames !== false,
      showCreatedDate: originalComponentProps?.showCreatedDate !== false,
      showPagination: originalComponentProps?.showPagination !== false,
      itemsPerPage: originalComponentProps?.itemsPerPage || 10,
      listLayout: originalComponentProps?.listLayout || 'card',
      showAvatars: originalComponentProps?.showAvatars || false,
      compactMode: originalComponentProps?.compactMode || false,
      backgroundColor: originalComponentProps?.backgroundColor || '#ffffff',
      borderColor: originalComponentProps?.borderColor || '#e0e0e0',
      textColor: originalComponentProps?.textColor || '#333333',
      titleColor: originalComponentProps?.titleColor || '#2c3e50',
      primaryColor: originalComponentProps?.primaryColor || '#3f51b5',
      secondaryColor: originalComponentProps?.secondaryColor || '#6c757d',
      successColor: originalComponentProps?.successColor || '#28a745',
      itemBackgroundColor: originalComponentProps?.itemBackgroundColor || '#ffffff',
      itemBorderColor: originalComponentProps?.itemBorderColor || '#e0e0e0',
      itemHoverColor: originalComponentProps?.itemHoverColor || '#f8f9fa',
      itemShadow: originalComponentProps?.itemShadow || '0 2px 4px rgba(0,0,0,0.1)',
      itemBorderRadius: originalComponentProps?.itemBorderRadius || '8px',
      itemPadding: originalComponentProps?.itemPadding || '16px',
      itemMargin: originalComponentProps?.itemMargin || '8px',
      titleFontSize: originalComponentProps?.titleFontSize || '24px',
      titleFontWeight: originalComponentProps?.titleFontWeight || 'bold',
      titleMarginBottom: originalComponentProps?.titleMarginBottom || '20px',
      nameFontSize: originalComponentProps?.nameFontSize || '16px',
      nameFontWeight: originalComponentProps?.nameFontWeight || '600',
      nameColor: originalComponentProps?.nameColor || '#2c3e50',
      dateFontSize: originalComponentProps?.dateFontSize || '14px',
      dateColor: originalComponentProps?.dateColor || '#6c757d',
      idFontSize: originalComponentProps?.idFontSize || '14px',
      idColor: originalComponentProps?.idColor || '#495057',
      paginationButtonColor: originalComponentProps?.paginationButtonColor || '#ffffff',
      paginationButtonBorderColor: originalComponentProps?.paginationButtonBorderColor || '#dee2e6',
      paginationActiveColor: originalComponentProps?.paginationActiveColor || '#3f51b5',
      paginationActiveTextColor: originalComponentProps?.paginationActiveTextColor || '#ffffff',
      paginationHoverColor: originalComponentProps?.paginationHoverColor || '#f8f9fa',
      loadingSpinnerColor: originalComponentProps?.loadingSpinnerColor || '#3f51b5',
      errorColor: originalComponentProps?.errorColor || '#dc3545',
      emptyStateColor: originalComponentProps?.emptyStateColor || '#6c757d'
    },
    content: { 
      ...originalContent 
    },
    pathVariables: originalProps?.pathVariables || {},
    id: originalProps?.id || '',
  };

  // 원본 props의 모든 속성 중 이미 처리한 속성 제외하고 나머지 복사
  const excludeKeys = ['content', 'style', 'className', 'data', 'componentProps', 'id', 'pathVariables'];
  
  Object.keys(originalProps).forEach(key => {
    if (!excludeKeys.includes(key)) {
      adaptedProps[key] = originalProps[key];
    }
  });

  return adaptedProps;
}

/**
 * 에디터용 데이터 형식 변환
 * 
 * @param originalProps 기존 컴포넌트의 props
 * @returns 변환된 에디터 컴포넌트용 props
 */
export function adaptRecommendedUsersEditorProps(originalProps: any): RecommendedUsersEditorProps {
  // 기본 props 변환 사용
  const adaptedProps = adaptRecommendedUsersProps(originalProps) as RecommendedUsersProps;
  
  // 에디터 관련 추가 props
  return {
    ...adaptedProps,
    isSelected: originalProps.isSelected || false,
    isPreview: originalProps.isPreview || false,
    viewMode: originalProps.viewMode || 'pc',
    updateComponent: originalProps.updateComponent || (() => {})
  };
} 